/**
 * AI 모델 정보 중앙 관리 (Single Source of Truth)
 * 
 * 새 모델이 나올 때마다 여기만 수정하면 됩니다!
 * 아래 파일들이 이 파일을 참조합니다:
 * - ChatPage (PRO AI 카테고리, 모델 드롭다운, SEO 메타태그)
 * - ModelSelector (모델 선택 드롭다운)
 * - PricingPage (FREE vs PRO 비교, SEO 메타태그)
 * - HelpCenterPage (FAQ, 모델 안내)
 * - ProSubscription (구독 혜택)
 * - PaymentSuccess (결제 완료 혜택 카드)
 * 
 * 마지막 업데이트: 2026-09-30 (GPT 칸·AI한글 GPT-6.1 Sol 전환)
 */

// GPT-5.6은 프로덕션 전면 배포 완료(master/test deploy 모두 true 주입).
// 기본 ON이며, 긴급 롤백 시에만 REACT_APP_CHAT_GPT56_ENABLED=false로 끈다.
export const CHAT_GPT56_ENABLED = process.env.REACT_APP_CHAT_GPT56_ENABLED !== 'false';

export const AI_MODELS = {
  gpt: {
    id: CHAT_GPT56_ENABLED ? 'gpt-5.6-luna' : 'gpt-5.5',
    // 서버가 GPT 슬롯을 gpt-6.1-sol(롤백 시 gpt-6-sol)로 승격한다. completion의 runtime ID를 UI ID로 되돌린다.
    runtimeIds: ['gpt-6.1-sol', 'gpt-6-sol'],
    latest: CHAT_GPT56_ENABLED ? 'GPT-6.1' : 'GPT-5.5',
    description: CHAT_GPT56_ENABLED
      ? '검증된 최신 추론 + 1M 컨텍스트 - 높은 품질과 효율'
      : '최강 추론 + 1M 컨텍스트 - OpenAI 프런티어 AI',
    chatDescription: '어려운 질문에도 정확하게 답해요',
    featured: true,
    isNew: true
  },
  hwp: {
    // wire ID는 구 backend 호환용. 서버가 document 카테고리를 CHAT_HWP_MODEL(gpt-6.1-sol)로 승격한다.
    id: CHAT_GPT56_ENABLED ? 'gpt-5.6-sol' : 'gpt-5.5',
    latest: CHAT_GPT56_ENABLED ? 'GPT-6.1 Sol' : 'GPT-5.5'
  },
  
  claude: {
    id: 'claude-sonnet-5',
    legacyIds: ['claude-sonnet-4-6', 'claude-sonnet-5-5'],
    latest: 'Claude Sonnet 5.5',
    description: '최고급 글쓰기 - 논리적 추론 특화',
    chatDescription: '최고급 글쓰기 - 논리적 추론에 특화돼요',
    featured: true,
    isNew: true
  },
  
  gemini: {
    id: 'gemini-3.1-pro',
    latest: 'Gemini 3.1 Pro',
    description: '최대 컨텍스트 - 200만 토큰 처리',
    chatDescription: '긴 자료도 한 번에 잘 이해해요',
    featured: true,
    isNew: false
  }
};

/**
 * 옵트인 추가 모델 (마케팅 화면에는 노출 안 함, 채팅 ModelSelector에만 노출)
 *
 * Gemini 3.8 Flash (2026-09-02 GA)
 * - 3.5/3.6/3.7 public ID는 기존 대화와 병렬 배포 호환용으로 유지
 * - /chat 옵트인만 교체. 비전·인스펙터·생기부·채점 모델에는 영향 없음
 *
 * Gemini 4 Argon (2026-10)
 * - Gemini 3.1 Pro 는 그대로 두고 옆에 추가한 옵트인 모델
 */
export const OPT_IN_PRO_MODELS = {
  geminiFlash: {
    id: 'gemini-3.8-flash',
    legacyIds: ['gemini-3.5-flash', 'gemini-3.6-flash', 'gemini-3.7-flash'],
    latest: 'Gemini 3.8 Flash',
    description: '복합 지식 작업에 강한 구글 최신 Flash 모델',
    chatDescription: '빠르고 정확하게 복잡한 질문도 해결해요'
  },
  geminiArgon: {
    id: 'gemini-4-argon',
    latest: 'Gemini 4 Argon',
    description: '깊은 추론과 긴 작업에 강한 구글 4세대 모델',
    chatDescription: '깊이 생각해서 어려운 작업도 해결해요'
  }
};

/**
 * Backend와 frontend가 병렬 배포되는 동안에도 새 UI가 구 backend에서
 * 무료 모델로 강등되지 않도록 Gemini/Claude 모두 기존 public ID를 wire에
 * 사용한다. 새 backend는 서버 정책에서 현재 운영 모델로 승격한다.
 */
export const toChatApiModelId = (modelId) => {
  if (modelId === OPT_IN_PRO_MODELS.geminiFlash.id) {
    return OPT_IN_PRO_MODELS.geminiFlash.legacyIds[0];
  }
  if (modelId === AI_MODELS.claude.id) {
    return AI_MODELS.claude.legacyIds[0];
  }
  return modelId;
};

/**
 * Firestore 기존 대화와 backend completion metadata의 레거시 ID를
 * 현재 사용자-facing 모델 ID로 정규화한다.
 */
export const normalizeChatModelId = (modelId) => {
  if (OPT_IN_PRO_MODELS.geminiFlash.legacyIds.includes(modelId)) {
    return OPT_IN_PRO_MODELS.geminiFlash.id;
  }
  if (AI_MODELS.claude.legacyIds.includes(modelId)) {
    return AI_MODELS.claude.id;
  }
  if (AI_MODELS.gpt.runtimeIds.includes(modelId)) {
    return AI_MODELS.gpt.id;
  }
  return modelId;
};

export const DEFAULT_CHAT_SLOT_IDS = [
  'gpt-5.4-nano',
  'gpt-5.4-mini',
];

export const GEMINI_DEFAULT_RUNTIME_IDS = [
  'gemini-3.1-flash-lite',
  'gemini-3.5-flash-lite',
];

/**
 * 대화 문서에는 backend runtime이 아니라 사용자가 선택한 UI 슬롯을 보존한다.
 * 그래야 고급 모드의 medium thinking이 재접속 후에도 유지된다.
 */
export const toPersistedChatSelection = (
  requestedModelId,
  effectiveModelId
) => normalizeChatModelId(
  requestedModelId || effectiveModelId || DEFAULT_CHAT_SLOT_IDS[0]
);

/**
 * 과거 runtime ID만 저장된 대화의 슬롯을 복구한다. 정확한 requested ID가
 * 메시지에 남아 있으면 ChatPage가 그 값을 우선 적용한다.
 */
export const restoreDefaultChatSlot = (modelId, isProUser = false) => (
  GEMINI_DEFAULT_RUNTIME_IDS.includes(modelId)
    ? (isProUser ? DEFAULT_CHAT_SLOT_IDS[1] : DEFAULT_CHAT_SLOT_IDS[0])
    : normalizeChatModelId(modelId)
);

/**
 * PRO 모델 ID 목록 (PRO 모델 체크용)
 * ChatPage에서 PRO 모델 여부를 확인할 때 사용
 *
 * 주의:
 * - 'gpt-5.4'는 레거시로 유지 (기존 PRO 사용자가 5.4로 시작한 세션 호환)
 * - mini/nano는 5.5에 변종이 없으므로 5.4-mini/nano 유지
 */
export const PRO_MODEL_IDS = [
  'gpt-5.4-mini',
  'gpt-5.4', // 레거시 - 기존 세션 호환 (드롭다운에서는 안 보이지만 PRO 권한 체크는 통과)
  'gpt-5.5', // 롤백 및 기존 대화 호환
  'gpt-5.6-sol',
  'gpt-5.6-terra',
  'gpt-5.6-luna',
  AI_MODELS.gpt.id,
  ...AI_MODELS.gpt.runtimeIds,
  AI_MODELS.claude.id,
  ...AI_MODELS.claude.legacyIds,
  'gemini-2.5-pro',
  AI_MODELS.gemini.id,
  // 옵트인 PRO 모델 + 기존 세션 호환 ID
  OPT_IN_PRO_MODELS.geminiFlash.id,
  ...OPT_IN_PRO_MODELS.geminiFlash.legacyIds,
  OPT_IN_PRO_MODELS.geminiArgon.id
];

/**
 * PRO 모델 목록 (· 구분, 카드/subtitle용)
 */
export const PRO_MODELS_SUBTITLE = `${AI_MODELS.gpt.latest} · ${AI_MODELS.gemini.latest} · ${AI_MODELS.claude.latest}`;

/**
 * PRO 모델 목록 (, 구분, 텍스트/SEO용)
 */
export const PRO_MODELS_TEXT = `${AI_MODELS.gpt.latest}, ${AI_MODELS.claude.latest}, ${AI_MODELS.gemini.latest}`;

/**
 * PRO 모델 목록 (상세 설명용)
 */
export const PRO_MODELS_DETAILED = [
  {
    name: AI_MODELS.gpt.latest,
    description: AI_MODELS.gpt.description
  },
  {
    name: AI_MODELS.claude.latest,
    description: AI_MODELS.claude.description
  },
  {
    name: AI_MODELS.gemini.latest,
    description: AI_MODELS.gemini.description
  }
];

/**
 * 기능 설명 텍스트
 */
export const AI_FEATURES = {
  memory: {
    free: '30개',
    pro: '100개'
  },
  fileAttachment: {
    free: '불가',
    pro: 'PDF, Excel, PPT, 이미지, 한글'
  },
  versionGeneration: {
    free: '2개',
    pro: '10개'
  },
  batchProcessing: {
    free: '단일만',
    pro: '엑셀 일괄'
  },
  webSearch: {
    free: '실시간 검색',
    pro: '실시간 검색'
  },
  pptDownload: {
    free: '글자형 PPTX · 8장',
    pro: '디자인형 · 40장'
  },
  worksheetImage: {
    free: '글자형 학습지',
    pro: '글자형 + 디자인형'
  },
  imageGeneration: {
    free: '기본 모델',
    pro: '고급 모델 · 참조 이미지'
  }
};

/**
 * PRO 구독 혜택 목록 (ProSubscription.js에서 사용)
 */
export const PRO_SUBSCRIPTION_FEATURES = [
  `${AI_MODELS.gpt.latest} - ${AI_MODELS.gpt.description}`,
  `${AI_MODELS.claude.latest} - ${AI_MODELS.claude.description}`,
  `${AI_MODELS.gemini.latest} - ${AI_MODELS.gemini.description}`,
  '신규 모델 출시 즉시 자동 업데이트',
  '모든 AI 모델 무제한 사용',
  '디자인형 AI PPT (40장까지)',
  '디자인형 AI 학습지',
  '고품질 AI 이미지 생성',
  '신규 기능 베타 우선 접근'
];

/**
 * 결제 완료 페이지 혜택 카드 (PaymentSuccess.js에서 사용)
 */
export const PRO_BENEFITS_CARDS = [
  {
    title: AI_MODELS.gpt.latest,
    description: AI_MODELS.gpt.description
  },
  {
    title: AI_MODELS.claude.latest,
    description: AI_MODELS.claude.description
  },
  {
    title: AI_MODELS.gemini.latest,
    description: AI_MODELS.gemini.description
  },
  {
    title: '디자인형 AI PPT',
    description: '디자인 PPT를 40장까지 만들어요'
  },
  {
    title: '디자인형 학습지',
    description: '디자인을 입힌 학습지로 받아요'
  },
  {
    title: '고품질 AI 이미지',
    description: '참조 이미지 기반 고품질 이미지 생성'
  },
  {
    title: '무제한 사용',
    description: '모든 AI 모델을 제한 없이 사용'
  }
];
